/**
 * Find-and-share search (Insights, 0.3.6): a company by ticker or name, or an
 * IR/PR firm by name. "/" focuses it from anywhere on the Insights pages;
 * arrows + Enter pick a result. Built for a salesperson on a live call.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Search, Users } from 'lucide-react';
import { searchInsights } from '../../lib/api/insights.js';

type Hit = { kind: 'company' | 'firm'; id: string; label: string; sub: string };

export function InsightsSearch({ autoFocus = false }: { autoFocus?: boolean }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const res = useQuery({
    queryKey: ['insights', 'search', debounced],
    queryFn: () => searchInsights(debounced),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });

  const hits: Hit[] = useMemo(() => {
    const d = res.data;
    if (!d) return [];
    return [
      ...d.companies.map((c) => ({
        kind: 'company' as const, id: c.entity_id,
        label: `${c.ticker ?? '—'} · ${c.name ?? ''}`, sub: `${c.articles} article${c.articles === 1 ? '' : 's'}`,
      })),
      ...d.firms.map((f) => ({
        kind: 'firm' as const, id: f.firm_id,
        label: f.name ?? f.domain ?? 'Firm', sub: `IR/PR firm · ${f.clients_current} current client${f.clients_current === 1 ? '' : 's'}`,
      })),
    ];
  }, [res.data]);

  useEffect(() => setActive(0), [debounced]);

  const go = (h: Hit) => {
    setOpen(false);
    setQ('');
    navigate(h.kind === 'company' ? `/insights/company/${h.id}` : `/insights/firm/${h.id}`);
  };

  return (
    <div className="relative w-full max-w-xl">
      <div className="flex items-center gap-2 rounded-xl border border-grey-200 bg-surface px-3 py-2 focus-within:ring-2 focus-within:ring-primary-300">
        <Search className="h-4 w-4 text-grey-400 flex-shrink-0" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            else if (e.key === 'Enter' && hits[active]) { e.preventDefault(); go(hits[active]); }
            else if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur(); }
          }}
          placeholder="Find a company (ticker or name) or a PR firm…"
          aria-label="Find a company or PR firm"
          className="w-full bg-transparent text-sm text-grey-900 placeholder:text-grey-400 focus:outline-none"
        />
        <kbd className="hidden sm:inline-block rounded border border-grey-200 px-1.5 text-[10px] text-grey-400">/</kbd>
      </div>
      {open && debounced.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-grey-200 bg-surface shadow-lg overflow-hidden">
          {res.isLoading && <div className="px-3 py-2 text-xs text-grey-400">Searching…</div>}
          {!res.isLoading && hits.length === 0 && <div className="px-3 py-2 text-xs text-grey-400">No company or firm matches “{debounced}”.</div>}
          {!res.isLoading && hits.length > 0 && res.data?.fuzzy && (
            <div className="px-3 pt-2 text-[11px] text-grey-500">No exact match for “{debounced}”. Did you mean:</div>
          )}
          <ul role="listbox">
            {hits.map((h, i) => (
              <li key={`${h.kind}-${h.id}`} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); go(h); }}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${i === active ? 'bg-primary-50' : ''}`}
                >
                  {h.kind === 'company' ? <Building2 className="h-4 w-4 text-grey-400" /> : <Users className="h-4 w-4 text-grey-400" />}
                  <span className="flex-1 min-w-0 truncate text-grey-900">{h.label}</span>
                  <span className="text-[11px] text-grey-500 whitespace-nowrap">{h.sub}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
